import { z, ZodIssueCode } from 'zod';
import { documentOptionalSchema, getDependentFieldOptionalSchema, getDependentFieldSchema, getDocumentSchema } from '../../../constants/sharedSchemas';
import { isBefore } from 'date-fns';

const jobExperiencesSchema = z.array(
  z.object({
    businessName: z.string().min(1, 'Business name is required'),
    locationCountry: z.string().min(1, 'Location is required'),
    titleOfPosition: z.string().min(1, 'Title of position is required'),
    dates: z
      .object({
        startDate: z.date({ required_error: 'Start date is required' }),
        endDate: z.date({ required_error: 'End date is required' }),
      })
      .refine(
        (data) => {
          // Throw error if end date is before start date
          return isBefore(data.startDate, data.endDate);
        },
        {
          message: 'End Date cannot be before Start Date',
          path: ['endDate'],
        },
      ),

    averageHoursPerWeek: z.string().min(1, 'This field is required'),
    descriptionOfDuties: z.string().min(1, 'This field is required'),
    employmentStatus: z.string().min(1, 'This field is required'),
  }),
);

const jobExperiencesOptionalSchema = z.array(
  z.object({
    businessName: z.string().optional(),
    locationCountry: z.string().optional(),
    titleOfPosition: z.string().optional(),
    dates: z
      .object({
        startDate: z.date().optional(),
        endDate: z.date().optional(),
      })
      .refine(
        (data) => {
          // Throw error if end date is before start date
          if (data.startDate && data.endDate) return isBefore(data.startDate, data.endDate);
          return true;
        },
        {
          message: 'End Date cannot be before Start Date',
          path: ['endDate'],
        },
      ),

    averageHoursPerWeek: z.string().optional(),
    descriptionOfDuties: z.string().optional(),
    employmentStatus: z.string().optional(),
  }),
);

const uniExperiencesSchema = z.array(
  z.object({
    nameOfUniversityCollege: z.string().min(1, 'Name of University/College is required'),
    locationCountry: z.string().min(1, 'Location is required'),
    major: z.string().min(1, 'Major is required'),
    degree: z.string().min(1, 'Degree is required'),
    statusOfDegree: z.string().min(1, 'Status of degree is required'),
  }),
);

const uniExperiencesOptionalSchema = z.array(
  z.object({
    nameOfUniversityCollege: z.string().optional(),
    locationCountry: z.string().optional(),
    major: z.string().optional(),
    degree: z.string().optional(),
    statusOfDegree: z.string().optional(),
  }),
);

const certificatesSchema = z.array(
  z.object({
    nameOfSchool: z.string().min(1, 'Name of school is required'),
    qualification: z.string().min(1, 'Qualification is required'),
    dateEarned: z.date({ required_error: 'Date earned is required' }),
    skillObtained: z.string().min(1, 'Skill is required'),
  }),
);

const certificatesOptionalSchema = z.array(
  z.object({
    nameOfSchool: z.string().optional(),
    qualification: z.string().optional(),
    dateEarned: z.date().optional(),
    skillObtained: z.string().optional(),
  }),
);

const resumeDocumentSchema = z.object({
  resumeType: z.literal('document'),
  document: getDocumentSchema('Resume'),
  linkedinUrl: z.string().optional(),
  jobExperiences: jobExperiencesOptionalSchema,
  uniExperiences: uniExperiencesOptionalSchema,
  certificates: certificatesOptionalSchema,
});

const resumeBuilderSchema = z.object({
  resumeType: z.literal('builder'),
  jobExperiences: jobExperiencesSchema,
  uniExperiences: uniExperiencesSchema,
  certificates: certificatesSchema,
  linkedinUrl: z.string().optional(),
  document: documentOptionalSchema,
});

const linkedinUrlSchema = z.object({
  resumeType: z.literal('linkedin'),
  linkedinUrl: z.string().min(1, 'LinkedIn Profile URL is required').url('URL address is invalid'),
  document: documentOptionalSchema,
  jobExperiences: jobExperiencesOptionalSchema,
  uniExperiences: uniExperiencesOptionalSchema,
  certificates: certificatesOptionalSchema,
});

const resumeSchema = z.discriminatedUnion('resumeType', [resumeDocumentSchema, resumeBuilderSchema, linkedinUrlSchema]);

export const yourExperienceSchema = {
  required: z.object({
    resume: resumeSchema,
    generalInformation: z.object({
      haveInternationalExperience: z.string().min(1, 'This field is required'),
      wantWorkWithExchangeStudents: z.string().min(1, 'This field is required'),
      haveExperienceWithTeenagers: z.string().min(1, 'This field is required'),
    }),
    mandatoryQuestions: z.object({
      hadPreviousAffiliation: getDependentFieldSchema('details', 'yes'),
      haveEverHostedStudent: z
        .object({
          value: z.string().min(1, 'Please specify'),
          duration: z.string().optional(),
          details: z.string().optional(),
        })
        .superRefine((data, ctx) => {
          // Make duration field required if the value is "yes"
          if (data.value === 'yes' && (!data.duration || data.duration?.trim() === '')) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: 'Please explain',
              path: ['duration'],
            });
          }
          if (data.value === 'yes' && (!data.details || data.details?.trim() === '')) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: 'Please explain',
              path: ['details'],
            });
          }
        }),
      contactsCPS: getDependentFieldSchema('explanation', 'yes'),
      haveEverBeenConvicted: getDependentFieldSchema('explanation', 'yes'),
    }),
  }),
  optional: z.object({
    resume: z.object({
      resumeType: z.union([z.literal('document'), z.literal('builder'), z.literal('linkedin')]),
      linkedinUrl: z.string().optional(),
      document: documentOptionalSchema,
      jobExperiences: jobExperiencesOptionalSchema,
      uniExperiences: uniExperiencesOptionalSchema,
      certificates: certificatesOptionalSchema,
    }),
    generalInformation: z.object({
      haveInternationalExperience: z.string().optional(),
      wantWorkWithExchangeStudents: z.string().optional(),
      haveExperienceWithTeenagers: z.string().optional(),
    }),
    mandatoryQuestions: z.object({
      hadPreviousAffiliation: getDependentFieldOptionalSchema('details'),
      haveEverHostedStudent: z.object({
        value: z.string().optional(),
        duration: z.string().optional(),
        details: z.string().optional(),
      }),
      contactsCPS: getDependentFieldOptionalSchema('explanation'),
      haveEverBeenConvicted: getDependentFieldOptionalSchema('explanation'),
    }),
  }),
};

export type YourExperienceFormValues = z.infer<(typeof yourExperienceSchema)['required']> | z.infer<(typeof yourExperienceSchema)['optional']>;
type JobExperience = YourExperienceFormValues['resume']['jobExperiences'][number];
type UniExperience = YourExperienceFormValues['resume']['uniExperiences'][number];
type Certificate = YourExperienceFormValues['resume']['certificates'][number];

export const jobExperienceDefaultValues: JobExperience = {
  businessName: '',
  locationCountry: '',
  titleOfPosition: '',
  dates: {
    startDate: undefined,
    endDate: undefined,
  },
  averageHoursPerWeek: '',
  descriptionOfDuties: '',
  employmentStatus: '',
};

export const uniExperienceDefaultValues: UniExperience = {
  nameOfUniversityCollege: '',
  locationCountry: '',
  major: '',
  degree: '',
  statusOfDegree: '',
};

export const certificateDefaultValues: Certificate = { nameOfSchool: '', qualification: '', dateEarned: undefined, skillObtained: '' };

export const defaultValues: YourExperienceFormValues = {
  resume: {
    resumeType: 'document',
    document: [],
    linkedinUrl: '',
    jobExperiences: [jobExperienceDefaultValues],
    uniExperiences: [uniExperienceDefaultValues],
    certificates: [certificateDefaultValues],
  },

  generalInformation: {
    haveInternationalExperience: '',
    wantWorkWithExchangeStudents: '',
    haveExperienceWithTeenagers: '',
  },
  mandatoryQuestions: {
    hadPreviousAffiliation: { value: '', details: '' },
    haveEverHostedStudent: { value: '', duration: '', details: '' },
    contactsCPS: { value: '', explanation: '' },
    haveEverBeenConvicted: { value: '', explanation: '' },
  },
};
